/**
 * The Security board's visual grammar, in one place so the KPI row, the bars,
 * the meters and the health panel can never disagree about what "bad" means.
 *
 * Threshold *policy* lives in the backend — `delivered_share_warn_pct`,
 * `delivered_share_danger_pct` and `mail_flow_floor` all arrive on the payload
 * rather than being invented here, so a number on screen and a number in an
 * alert can't drift apart. This module only maps a value onto design-system
 * tones and formats it for a human.
 */

import type { QuarterStatement, SecurityHealth, SecurityOverview } from "@/lib/api";

/** Tone shared by `Badge`, `Statistic`, `Meter` and `MiniSparkline`. */
export type StatusTone = "success" | "warning" | "danger" | "neutral";

/**
 * Segment colours for the classification donut, from the design system's
 * categorical chart tokens. Fixed assignment, so a class that drops to zero in
 * one window keeps its colour rather than letting the others shift (dashboard
 * design rules §3.3). Three classes by construction — the pinned definition has
 * exactly three — so the donut can never breach the ≤5-segment rule.
 */
export const CLASSIFICATION_COLOR: Record<string, string> = {
  malware: "var(--norican-chart-1)",
  impostor: "var(--norican-chart-2)",
  phish: "var(--norican-chart-3)",
};

/** Segment colours for the blocked/delivered composition. */
export const DIRECTION_COLOR = {
  blocked: "var(--norican-success)",
  delivered: "var(--norican-danger)",
} as const;

/** How long after its poll interval a successful poll counts as stale. */
const STALE_MULTIPLIER = 2.5;

/** A human label for the backend's `last_status` vocabulary. */
export const POLL_STATUS_LABEL: Record<string, string> = {
  ok: "Collecting normally",
  never_run: "Has never run",
  not_configured: "No credential",
  circuit_open: "Backing off after a rate limit",
  rate_limited: "Rate-limited by Proofpoint",
  auth_failed: "Credential rejected",
  unreachable: "Cannot reach Proofpoint",
  no_window_due: "Waiting for the next window",
  store_unavailable: "Cannot reach the rollup store",
  failed: "Failed unexpectedly",
};

/** Tone for a poll status. Only `ok` is reassuring. */
export function pollStatusTone(status: string): StatusTone {
  if (status === "ok") {
    return "success";
  }
  if (status === "no_window_due") {
    return "neutral";
  }
  if (status === "circuit_open" || status === "rate_limited") {
    return "warning";
  }
  return "danger";
}

/**
 * Tone for one day's mail-flow volume against the expected floor.
 *
 * Only a *complete* day can breach the floor — today is still accumulating, and
 * judging it would raise a P1 every morning.
 */
export function mailFlowTone(total: number, floor: number | null, isComplete: boolean): StatusTone {
  if (!isComplete || floor === null) {
    return "neutral";
  }
  return total < floor ? "danger" : "success";
}

/** Whether the last successful poll is old enough to distrust the numbers. */
export function isPollStale(health: SecurityHealth, now: number = Date.now()): boolean {
  if (!health.last_success_at) {
    return true;
  }
  const age = now - new Date(health.last_success_at).getTime();
  return age > health.siem_poll_interval_sec * 1000 * STALE_MULTIPLIER;
}

/** Milliseconds after which the freshness dot escalates on its own. */
export function staleAfterMs(health: SecurityHealth): number {
  return health.siem_poll_interval_sec * 1000 * STALE_MULTIPLIER;
}

/**
 * Whether the board's figures should be read as current at all.
 *
 * A silently dead poller rendering a reassuring zero is worse than no panel, so
 * every panel that shows a count consults this and captions itself accordingly.
 */
export function figuresAreCurrent(overview: SecurityOverview): boolean {
  return (
    overview.configured &&
    !overview.health.budget.circuit_open &&
    !overview.health.store_detail &&
    !isPollStale(overview.health)
  );
}

/** Format a whole-number count with thousands separators. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return value.toLocaleString();
}

/** Format a percentage, or an em dash when there is genuinely no ratio. */
export function formatPct(pct: number | null | undefined, digits = 1): string {
  if (pct === null || pct === undefined) {
    return "—";
  }
  return `${pct.toFixed(digits)}%`;
}

/** Format an ISO timestamp as a short local date and time. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Format an ISO date (`2026-08-20`) as a short day label for an axis. */
export function formatDayLabel(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return day;
  }
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** Format an ISO date as a two-letter weekday, for a dense heat row. */
export function formatDayInitial(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return "?";
  }
  return parsed.toLocaleDateString(undefined, { weekday: "narrow" });
}

/** Format a duration in seconds as a coarse "4h 12m" / "9m" / "45s". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return "—";
  }
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${Math.round(seconds)}s`;
}

/* ------------------------------------------------------------------------ *
 * The executive half of the board
 *
 * These came from the retired `/proofpoint` board when the two merged. Tone on
 * this board is inverted from the usual reading — a big blocked count is good
 * news and a small delivered count is bad news — so every mapping is explicit
 * rather than inferred from magnitude.
 * ------------------------------------------------------------------------ */

/** The collector polls every 5 minutes; flag executive figures older than two cycles. */
export const EXEC_STALE_AFTER_MS = 10 * 60_000;

/** A 0–100 value as a percentage with two decimals — the deck quotes 99.9%+. */
export function formatRate(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : `${value.toFixed(2)}%`;
}

/**
 * Tone for "incidents with business impact". Anything above zero is danger —
 * never warning, because a payload that reached a mailbox is not a degraded
 * control, it is a failed one.
 *
 * Zero is `neutral`, **not** `success`. Zero is the state the deck asserts is
 * normal, so colouring it green would sell the absence of a failure as an
 * achievement and make the abnormal reading look like a regression from a win
 * rather than the incident it is. The tile's own comment in `exec-tiles.tsx`
 * states this ("never green"); this function is where it is actually enforced.
 */
export function reachedUserTone(reached: number): StatusTone {
  return reached === 0 ? "neutral" : "danger";
}

/** Tone for the block-rate meter, against the deck's own 99.9%+ claim. */
export function blockRateTone(rate: number | null | undefined): StatusTone {
  if (rate === null || rate === undefined) {
    return "neutral";
  }
  if (rate >= 99.9) {
    return "success";
  }
  return rate >= 99 ? "warning" : "danger";
}

/**
 * Coverage tone: a headline computed from a third of the window is not the same
 * claim as one computed from all of it, and the caption should say so.
 */
export function coverageTone(coveragePct: number | null | undefined): StatusTone {
  if (coveragePct === null || coveragePct === undefined) {
    return "neutral";
  }
  if (coveragePct >= 90) {
    return "success";
  }
  return coveragePct >= 50 ? "warning" : "danger";
}

/** Map a backend statement tone onto a `Statistic` tone. */
export function statementTone(statement: QuarterStatement): StatusTone {
  switch (statement.tone) {
    case "positive":
      return "success";
    case "caution":
      return "warning";
    case "danger":
      return "danger";
    default:
      return "neutral";
  }
}

/** `Meter` speaks `accent` where `Statistic` speaks `neutral`. */
export function meterTone(tone: StatusTone): "success" | "warning" | "danger" | "accent" {
  return tone === "neutral" ? "accent" : tone;
}
